import React, { useState, useEffect } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowLeft, faCalendar } from '@fortawesome/free-solid-svg-icons'
import { format } from 'date-fns'
import LayoutSingleViewFullWidth from '../common/layout/LayoutSingleViewFullWidth'
import { MenuItems } from '../../enums/MenuItems'
import Badge from '../common/badge/Badge'
import { BadgeColor } from '../common/badge/badgeTypes'
import { Pages } from '../../Pages'
import CandidatesTable from './components/CandidatesTable'
import kitApi, { type GetScreenerDetailUserRes } from '../../api/kit/KitApi'
import type { Rubric, Candidate } from './types'
import { mapApiCandidate } from './types'
import { buildApplyUrl } from './utils'
import LinkCopyButton from './components/LinkCopyButton'

export default function JobScreenerDetail(): React.JSX.Element {
    const [searchParams] = useSearchParams()
    const id = searchParams.get('id')

    const [data, setData] = useState<GetScreenerDetailUserRes | null>(null)
    const [isLoading, setIsLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)

    useEffect(() => {
        if (!id) {
            setIsLoading(false)
            return
        }
        async function load() {
            try {
                setIsLoading(true)
                const res = await kitApi.getScreenerDetailForUser(id!)
                setData(res)
            } catch {
                setError('Unable to load screener details.')
            } finally {
                setIsLoading(false)
            }
        }
        void load()
    }, [id])

    // Map API data to component data
    const rubric: Rubric = data
        ? {
              sections: data.rubric.sections.map((s) => ({
                  id: s.id,
                  label: s.label,
                  criteria: s.criteria.map((c) => ({
                      id: c.id,
                      label: c.label,
                      shortName: c.shortName,
                      scoreType: s.scoreType,
                  })),
              })),
          }
        : { sections: [] }

    const candidates: Candidate[] = data
        ? data.candidates.map(mapApiCandidate)
        : []

    // Not found state
    if (!id || (!data && !isLoading && !error)) {
        return (
            <LayoutSingleViewFullWidth activeMenuItem={MenuItems.JobScreeners}>
                <div className="py-6">
                    <Link
                        to={Pages.JobScreeners}
                        className="text-ctxt-blue hover:text-ctxt-blue/80 text-sm inline-flex items-center gap-2 font-medium transition-colors"
                    >
                        <FontAwesomeIcon icon={faArrowLeft} className="w-3" />
                        Back to Job Screeners
                    </Link>
                    <div className="text-center py-20 mt-8">
                        <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
                            <span className="text-2xl text-gray-300">?</span>
                        </div>
                        <p className="text-lg font-semibold text-gray-600">
                            Screener not found
                        </p>
                        <p className="text-sm text-gray-400 mt-1.5 max-w-sm mx-auto">
                            The screener you are looking for does not exist or
                            has been removed.
                        </p>
                    </div>
                </div>
            </LayoutSingleViewFullWidth>
        )
    }

    // Loading state
    if (isLoading) {
        return (
            <LayoutSingleViewFullWidth activeMenuItem={MenuItems.JobScreeners}>
                <div className="py-6">
                    <Link
                        to={Pages.JobScreeners}
                        className="text-ctxt-blue hover:text-ctxt-blue/80 text-sm inline-flex items-center gap-2 font-medium transition-colors"
                    >
                        <FontAwesomeIcon icon={faArrowLeft} className="w-3" />
                        Back to Job Screeners
                    </Link>
                    <div className="flex justify-center py-20">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-ctxt-blue" />
                    </div>
                </div>
            </LayoutSingleViewFullWidth>
        )
    }

    // Error state
    if (error) {
        return (
            <LayoutSingleViewFullWidth activeMenuItem={MenuItems.JobScreeners}>
                <div className="py-6">
                    <Link
                        to={Pages.JobScreeners}
                        className="text-ctxt-blue hover:text-ctxt-blue/80 text-sm inline-flex items-center gap-2 font-medium transition-colors"
                    >
                        <FontAwesomeIcon icon={faArrowLeft} className="w-3" />
                        Back to Job Screeners
                    </Link>
                    <div className="text-center py-20">
                        <p className="text-sm font-medium text-red-500">
                            {error}
                        </p>
                    </div>
                </div>
            </LayoutSingleViewFullWidth>
        )
    }

    const applyUrl = data ? buildApplyUrl(data.urlId) : ''

    return (
        <LayoutSingleViewFullWidth activeMenuItem={MenuItems.JobScreeners}>
            <div className="py-6">
                {/* Back link */}
                <Link
                    to={Pages.JobScreeners}
                    className="text-ctxt-blue hover:text-ctxt-blue/80 text-sm inline-flex items-center gap-2 font-medium transition-colors mb-5"
                >
                    <FontAwesomeIcon icon={faArrowLeft} className="w-3" />
                    Back to Job Screeners
                </Link>

                {/* Page header */}
                <div className="mb-8">
                    <div className="flex items-start justify-between flex-wrap gap-4">
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900 tracking-tight">
                                {data!.jobTitle}
                            </h1>
                        </div>
                        <Badge
                            color={
                                data!.isActive
                                    ? BadgeColor.Success
                                    : BadgeColor.Neutral
                            }
                        >
                            {data!.isActive ? 'Active' : 'Inactive'}
                        </Badge>
                    </div>

                    {/* Meta row */}
                    <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mt-3">
                        <span className="inline-flex items-center gap-1.5 text-sm text-gray-500">
                            <FontAwesomeIcon
                                icon={faCalendar}
                                className="w-3 h-3 text-gray-400"
                            />
                            Created{' '}
                            {format(new Date(data!.createdAt), 'd MMM yyyy')}
                        </span>
                        {applyUrl && <LinkCopyButton url={applyUrl} />}
                    </div>
                </div>

                {/* Candidates heading */}
                <div className="flex items-end justify-between flex-wrap gap-3 mb-4">
                    <div>
                        <h2 className="text-lg font-bold text-gray-800">
                            Candidates
                        </h2>
                        <p className="text-sm text-gray-500 mt-0.5">
                            {candidates.length === 0
                                ? 'No scored candidates yet'
                                : `${candidates.length} candidate${candidates.length === 1 ? '' : 's'} screened`}
                        </p>
                    </div>
                </div>

                {/* Empty candidate state with apply URL guidance */}
                {candidates.length === 0 ? (
                    <div className="border border-gray-200 rounded-lg bg-white">
                        <div className="text-center py-16 px-6">
                            <div className="w-14 h-14 rounded-full bg-gray-50 flex items-center justify-center mx-auto mb-4">
                                <span className="text-xl text-gray-300">0</span>
                            </div>
                            <p className="text-base font-semibold text-gray-500">
                                No scored candidates yet
                            </p>
                            <p className="text-sm text-gray-400 mt-1.5 max-w-xs mx-auto">
                                Candidates will appear here after they complete
                                the screener and are scored.
                            </p>
                            {applyUrl && (
                                <div className="mt-3">
                                    <LinkCopyButton url={applyUrl} />
                                </div>
                            )}
                        </div>
                    </div>
                ) : (
                    <CandidatesTable rubric={rubric} candidates={candidates} />
                )}
            </div>
        </LayoutSingleViewFullWidth>
    )
}
